import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoiceDollar, faCheck, faExclamationTriangle, faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../utils/axiosConfig';
import { useFlashMessage } from '../../components/FlashMessage/FlashMessageContext';

/**
 * Accounting Queue — "Awaiting accounting" (the last link of the invoice
 * workflow chain). Every APPROVED money document not yet collected by
 * accounting, across all four bookable types, with a per-row bookability
 * verdict. "Mark as collected" drains the queue and stamps
 * handed_to_accounting_at / collected_by on the document.
 */
const AccountingQueue = () => {
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();
  const [rows, setRows] = useState([]);
  const [notBookable, setNotBookable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(null); // `${type}:${id}` in flight

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/api/invoicing/accounting-queue/');
      setRows(data.results || []);
      setNotBookable(data.not_bookable_count || 0);
    } catch (error) {
      console.error('Error loading accounting queue:', error);
      showError('Failed to load the accounting queue.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markCollected = async (row) => {
    const key = `${row.document_type}:${row.id}`;
    setCollecting(key);
    try {
      const { data } = await axiosInstance.post('/api/invoicing/accounting-queue/mark-collected/', {
        document_type: row.document_type,
        id: row.id,
      });
      showSuccess(data.message || `${row.number} marked as collected.`);
      setRows((prev) => prev.filter((r) => !(r.document_type === row.document_type && r.id === row.id)));
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to mark as collected.');
    } finally {
      setCollecting(null);
    }
  };

  const formatAmount = (v) => {
    const num = parseFloat(v);
    return Number.isNaN(num)
      ? v
      : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <Layout>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
          <button
            type="button"
            onClick={() => history.push('/invoicing/dashboard')}
            style={{
              border: '1px solid #d0d0d0', background: 'white', borderRadius: '6px',
              padding: '6px 10px', cursor: 'pointer',
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h1 style={{ margin: 0, fontSize: '22px' }}>
            <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ marginRight: '8px' }} />
            Awaiting Accounting
          </h1>
        </div>
        <p style={{ color: '#666', marginTop: '4px', marginBottom: '16px' }}>
          Approved money documents not yet collected by accounting.
          {notBookable > 0 && (
            <span style={{ color: '#c62828', fontWeight: 600 }}>
              {' '}{notBookable} cannot be booked yet (missing coding).
            </span>
          )}
        </p>

        {loading ? (
          <div style={{ padding: '30px', color: '#888' }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{
            padding: '30px', textAlign: 'center', color: '#666',
            background: '#f7faf7', border: '1px solid #d8ecd8', borderRadius: '8px',
          }}>
            <FontAwesomeIcon icon={faCheck} style={{ marginRight: '8px', color: '#2e7d32' }} />
            Queue is empty — everything approved has been collected.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
              <thead>
                <tr style={{ background: '#1a3c6e', color: 'white', textAlign: 'left' }}>
                  <th style={{ padding: '10px' }}>Type</th>
                  <th style={{ padding: '10px' }}>Number</th>
                  <th style={{ padding: '10px' }}>Partner</th>
                  <th style={{ padding: '10px' }}>Entity</th>
                  <th style={{ padding: '10px' }}>Date</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: '10px' }}>Bookable</th>
                  <th style={{ padding: '10px' }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const key = `${row.document_type}:${row.id}`;
                  return (
                    <tr key={key} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px' }}>{row.document_label}</td>
                      <td style={{ padding: '10px' }}>
                        <button
                          type="button"
                          onClick={() => history.push(row.detail_url)}
                          style={{
                            background: 'none', border: 'none', color: '#5a7b35',
                            cursor: 'pointer', fontWeight: 600, padding: 0,
                          }}
                        >
                          {row.number}
                        </button>
                      </td>
                      <td style={{ padding: '10px' }}>{row.partner_name}</td>
                      <td style={{ padding: '10px' }}>{row.hms_entity || '—'}</td>
                      <td style={{ padding: '10px' }}>{row.invoice_date || '—'}</td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>
                        {row.currency} {formatAmount(row.gross_amount)}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {row.bookable ? (
                          <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                            <FontAwesomeIcon icon={faCheck} /> Ready
                          </span>
                        ) : (
                          <span title={row.missing.join('; ')} style={{ color: '#c62828', fontWeight: 600 }}>
                            <FontAwesomeIcon icon={faExclamationTriangle} /> Missing: {row.missing.join('; ')}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => markCollected(row)}
                          disabled={collecting === key}
                          style={{
                            padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
                            background: '#5a7b35', color: 'white', border: 'none', fontWeight: 600,
                          }}
                        >
                          {collecting === key ? 'Collecting…' : 'Mark as collected'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AccountingQueue;
